import { Form, Input, Button, message } from "antd";
import { useForm } from "antd/lib/form/Form";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { DraggableModal } from "../../../../../components/DraggableModal";
import { EntProduct } from "../../../../../models/handBooks/product";
import SelectProduct from "../../../../../components/selects/SelectProduct/SelectProduct";

interface IProps {
  show: boolean;
  productCategory: EntProduct;
  onHide: () => void;
  onSubmit: (pos: EntProduct) => Promise<boolean>;
}

enum FormField {
  nameKy = 'nameKy',
  nameRu = 'nameRu',
  categoryId = 'categoryId'
}

export const AddProduct = (props: IProps) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState<boolean>(false);
  const [productData, setProductData] = useState<{
    parent: { id: number; childId: number | null } | null;
    varieties: Array<{ nameRu: string; nameKy: string }>;
    categoryId: number | null;
  }>({
    parent: props.productCategory.category ? 
      props.productCategory.category.parentId === null ? 
        { id: props.productCategory.category.id, childId: null } :
        {
          id: props.productCategory.category.parentId || (props.productCategory.parent?.id ?? 0),
          childId: props.productCategory.category.id || props.productCategory.parent?.childId || null
        }
      : props.productCategory.parent ? {
        id: props.productCategory.parent.id,
        childId: props.productCategory.parent.childId || null
      } : null,
    varieties: props.productCategory.varieties?.map(variety => ({
      nameRu: variety.nameRu || '',
      nameKy: variety.nameKy || ''
    })) || [],
    categoryId: props.productCategory.categoryId || null
  });
  const [isProductValid, setIsProductValid] = useState(false);

  useEffect(() => {
    if (props.show) {
      form.setFields([
        { name: FormField.nameKy, value: props.productCategory.nameKy },
        { name: FormField.nameRu, value: props.productCategory.nameRu },
        { name: FormField.categoryId, value: props.productCategory.categoryId }
      ]);
      console.log('Setting product data:', props.productCategory);
      setProductData({
        parent: props.productCategory.category ? 
          props.productCategory.category.parentId === null ? 
            { id: props.productCategory.category.id, childId: null } :
            {
              id: props.productCategory.category.parentId || (props.productCategory.parent?.id ?? 0),
              childId: props.productCategory.category.id || props.productCategory.parent?.childId || null
            }
          : props.productCategory.parent ? {
            id: props.productCategory.parent.id,
            childId: props.productCategory.parent.childId || null
          } : null,
        varieties: props.productCategory.varieties?.map(variety => ({
          nameRu: variety.nameRu || '',
          nameKy: variety.nameKy || ''
        })) || [],
        categoryId: props.productCategory.categoryId || null
      });
    }
  }, [props.show, props.productCategory]);

  const handleSubmit = async () => {
    try {
      if (!isProductValid) {
        message.error('Пожалуйста, заполните все обязательные поля в категориях');
        return;
      }

      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();

      const data: EntProduct = {
        id: props.productCategory?.id || null,
        nameKy: values.nameKy,
        nameRu: values.nameRu,
        categoryId: productData.parent?.childId || productData.categoryId,
        varieties: productData.varieties.map(sub => ({
          nameKy: sub.nameKy,
          nameRu: sub.nameRu,
          id: null
        })),
        parent: productData.parent
      };
      
      const result = await props.onSubmit(data);
      if (result) {
        props.onHide();
        setProductData({ parent: null, varieties: [], categoryId: null });
        form.resetFields();
      }
    } catch (error) {
      console.error('Ошибка при отправке:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleProductDataChange = async (value: {
    parent: { id: number; childId: number | null } | null;
    varieties: Array<{ nameRu: string; nameKy: string }>;
    categoryId: number | null;
  }) => {
    console.log('Получены данные от SelectProduct:', value);
    setProductData(value);

  
    if (value.varieties.length < (productData.varieties?.length || 0)) {
      try {
        const data: EntProduct = {
          id: props.productCategory?.id || null,
          nameKy: form.getFieldValue(FormField.nameKy),
          nameRu: form.getFieldValue(FormField.nameRu),
          categoryId: value.parent?.childId || value.categoryId,
          varieties: value.varieties.map(sub => ({
            nameKy: sub.nameKy,
            nameRu: sub.nameRu,
            id: null
          })),
          parent: value.parent
        };
        
        await props.onSubmit(data);
      } catch (error) {
        console.error('Ошибка при сохранении после удаления:', error);
      }
    }
  };

  return (
    <>
      <DraggableModal
        title={props.productCategory.id === null ? t('handbooks.types_of_products.Add Product') : t('handbooks.types_of_products.Edit Product Type')}
        visible={props.show}
        onCancel={() => {
          props.onHide();
          setProductData({ parent: null, varieties: [], categoryId: null });
          form.resetFields();
        }}
        noFooter
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{ remember: true }}
          autoComplete="off"
          onFinish={handleSubmit}
        >
          <Form.Item
            label={t('general.nameKy')}
            name={FormField.nameKy}
            rules={[{ required: true, message: t("msg.fill_in_the_field") }]}
          >
            <Input autoComplete="off" />
          </Form.Item>
          <Form.Item
            label={t('general.nameRu')}
            name={FormField.nameRu}
            rules={[{ required: true, message: t("msg.fill_in_the_field") }]}
          >
            <Input autoComplete="off" />
          </Form.Item>

          <SelectProduct
            value={productData}
            onChange={handleProductDataChange} 
            onValidityChange={setIsProductValid}
          />

          <Form.Item style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              disabled={!isProductValid}
            >
              {t("general.create")}
            </Button>
          </Form.Item>
        </Form>
      </DraggableModal>
    </>
  );
};
